import { FC, useEffect, useState } from 'react';
import { from } from 'rxjs';
import { matchRoutes, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { sidebarCollapsedSelector } from '@store/slices/portal.slice';
import { ROUTES } from '@routes';
import { useService } from '@hooks';
import { IRoute, PageType } from '@models';
import { authService, userPreferencesService, userService } from '@services';
import { Grid } from '@mui/material';
import { LoadingLayout } from '@components/common';
import { Sidebar } from '../Sidebar';
import { GuardedRender } from '../GuardedRender';
import { TermsAndConditionsDialog } from '../TermsAndConditionsDialog';
import { MobileDeviceNotificationDialog } from '../MobileDeviceNotificationDialog';

export const PortalLayout: FC = () => {
	const loc = useLocation();
	const navigate = useNavigate();
	const sidebarCollapsed = useSelector(sidebarCollapsedSelector);
	const [pageType, setPageType] = useState<PageType>(PageType.STANDARD);
	const { loading, error } = useService(() => from(userService.doIfNotAdmin(userService.getMe())));
	useService(() => from(userService.doIfNotAdmin(userPreferencesService.find())));
	const isSidebarVisible = pageType !== PageType.FULLSCREEN;

	useEffect(() => {
		const matches = matchRoutes(ROUTES, loc);
		const route = matches?.slice(-1)?.[0].route as IRoute;
		setPageType(route.pageType || PageType.STANDARD);
	}, [loc]);

	if (error && !authService.isAdmin()) {
		authService.logout().then(() => navigate('/login'));
		return <LoadingLayout />;
	}

	if (loading) {
		return <LoadingLayout />;
	}

	return (
		<Grid container sx={{ flexGrow: 1, flexWrap: 'nowrap', backgroundColor: 'background.default', maxHeight: 1.0, overflow: 'hidden' }}>
			{isSidebarVisible ? (
				<Grid item sx={{ width: sidebarCollapsed ? 100 : 300, maxHeight: 1.0, transition: 'all 0.25s' }}>
					<Sidebar />
				</Grid>
			) : null}

			<Grid item xs sx={{ overflowX: 'hidden', overflowY: 'auto', maxHeight: 1.0 }}>
				<Outlet />
				<GuardedRender canRender={(user) => user.termsAccepted === false}>
					<TermsAndConditionsDialog />
				</GuardedRender>
				<MobileDeviceNotificationDialog />
			</Grid>
		</Grid>
	);
};
